import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { productPreviews } from '../data/productPreviews';
import type { ProductPreview } from '../data/productPreviews';
const EASE = [0.23, 1, 0.32, 1] as const;
export function ProductCarousel() {
  const [index, setIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const total = productPreviews.length;
  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  const go = (direction: number) => setIndex(current => (current + direction + total) % total);
  const offsetOf = (itemIndex: number) => {
    let offset = itemIndex - index;
    if (offset > total / 2) offset -= total;
    if (offset < -total / 2) offset += total;
    return offset;
  };
  return <section className="py-16 md:py-24 bg-canvas overflow-hidden" aria-labelledby="inside-title" data-id="element-210" data-mp="37972ua8o7woz">
      <div className="max-w-6xl mx-auto px-4" data-id="element-211" data-mp="32c6wdy7wsrxc">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14" data-id="element-212" data-mp="32di05y1ctzog">
          <p className="inline-block px-3 py-1 rounded-full border border-gold/50 text-gold text-xs font-semibold tracking-widest mb-4" data-id="element-213" data-mp="2xiuncwfpqtpp">
            A LOOK INSIDE THE 6-IN-1 SYSTEM
          </p>
          <h2 id="inside-title" className="text-3xl md:text-5xl font-display font-bold text-white mb-4" data-id="element-214" data-mp="2nnsx1x9pbllj">
            See what&apos;s inside Boxing Coach Ready
          </h2>
          <p className="text-mist" data-id="element-215" data-mp="2ekrsi10hi2jz">
            A closer look at the visual coaching material designed to help you plan faster, teach
            with more structure, and bring more variety into every boxing session.
          </p>
        </div>

        <div className="relative" role="region" aria-roledescription="carousel" aria-label="Product preview" data-id="element-216" data-mp="cs3vwaicidd6">
          <motion.div className="relative mx-auto w-[84%] max-w-[380px] md:w-[360px] cursor-grab active:cursor-grabbing" drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.18} onDragEnd={(_, info) => {
          if (info.offset.x < -50) go(1);else if (info.offset.x > 50) go(-1);
        }} data-id="element-217" data-mp="7tahmfv8iiby">
            <div className="invisible" aria-hidden="true" data-id="element-218" data-mp="1x9w8odngs8t8">
              <PreviewCard preview={productPreviews[0]} active data-id="element-219" data-mp="2l34p26s6ocmi" />
            </div>
            {productPreviews.map((preview, itemIndex) => {
            const offset = offsetOf(itemIndex);
            const isActive = offset === 0;
            const isVisible = isDesktop ? Math.abs(offset) <= 1 : isActive;
            return <motion.div key={preview.src} className="absolute inset-0" initial={false} animate={{
              x: `${offset * (isDesktop ? 110 : 108)}%`,
              scale: isActive ? 1 : 0.86,
              opacity: isVisible ? isActive ? 1 : 0.5 : 0
            }} transition={{
              duration: 0.3,
              ease: EASE
            }} style={{
              zIndex: isActive ? 2 : 1,
              pointerEvents: isVisible ? 'auto' : 'none'
            }} aria-hidden={!isActive} onClick={() => !isActive && isVisible && go(offset)} data-id="element-220" data-mp="2g4jmi0pglguw" data-mpi={itemIndex}>
                  <PreviewCard preview={preview} active={isActive} data-id="element-221" data-mp="305mij5kbl0ue" />
                </motion.div>;
          })}
          </motion.div>

          <button type="button" onClick={() => go(-1)} className="hidden md:flex absolute left-0 lg:left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border border-gold/50 bg-card text-gold items-center justify-center hover:bg-card-hi transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" aria-label="Previous preview" data-id="element-222" data-mp="38x50nm0ttqq4">
            <ChevronLeft size={22} data-id="element-223" data-mp="2p5stja6kwdk0" />
          </button>
          <button type="button" onClick={() => go(1)} className="hidden md:flex absolute right-0 lg:right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border border-gold/50 bg-card text-gold items-center justify-center hover:bg-card-hi transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" aria-label="Next preview" data-id="element-224" data-mp="2u3b7jezgdiyv">
            <ChevronRight size={22} data-id="element-225" data-mp="3cshi2eq3f49l" />
          </button>

          <div className="flex md:hidden justify-center gap-2 mt-6" data-id="element-226" data-mp="3htf1svqr46d7">
            {productPreviews.map((preview, dotIndex) => <button key={preview.src} type="button" onClick={() => setIndex(dotIndex)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${dotIndex === index ? 'w-6 bg-gold' : 'w-2 bg-line'}`} aria-label={`Show preview ${dotIndex + 1}`} aria-current={dotIndex === index} data-id="element-227" data-mp="3mr6ye32ccgnq" data-mpi={dotIndex} />)}
          </div>
        </div>

        <div className="text-center mt-12 md:mt-16" data-id="element-228" data-mp="1b5oekt387qzl">
          <div className="w-16 h-px bg-gold mx-auto mb-6" aria-hidden="true" data-id="element-229" data-mp="165tpmv30hbh6" />
          <p className="text-2xl md:text-3xl font-display font-bold text-white mb-2" data-id="element-230" data-mp="163pblkbeew36">
            6 Complete Volumes. <span className="text-gold" data-id="element-231" data-mp="23r7rdf3h8ygo">One Visual Coaching System.</span>
          </p>
          <p className="text-mist" data-id="element-232" data-mp="1ysed3kgd93fg">
            Built to help boxing coaches spend less time planning and more time coaching.
          </p>
        </div>
      </div>
    </section>;
}
function PreviewCard({
  preview,
  active
}: {
  preview: ProductPreview;
  active: boolean;
}) {
  return <figure className={`bg-card border rounded-2xl p-3 shadow-[0_24px_50px_-24px_rgba(0,0,0,0.9)] transition-colors duration-200 ease-out ${active ? 'border-gold/60' : 'border-gold/20'}`} data-id="element-233" data-mp="ihsh3m5u1v1r">
      <div className="rounded-xl overflow-hidden bg-white aspect-[4/5]" data-id="element-234" data-mp="31kyxux4letkw">
        <img src={preview.src} alt={preview.alt} draggable={false} className="w-full h-full object-contain select-none" data-id="element-235" data-mp="35o52y1fyyeps" />
      </div>
      <figcaption className="pt-3 pb-1 text-center font-display text-sm tracking-wider text-white uppercase" data-id="element-236" data-mp="2wmxiam6dlce6">
        {preview.label}
      </figcaption>
    </figure>;
}